import React, { useEffect, useState } from 'react';
import { collection, query, where, orderBy, getDocs, doc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useHeader } from '../../contexts/HeaderContext';

// 报销状态枚举
enum ReimbursementStatus {
    PENDING = "pending", // 待审核
    APPROVED = "approved", // 已批准
    REJECTED = "rejected", // 已拒绝
    PAID = "paid" // 已支付
}

// 报销类型枚举
enum ReimbursementType {
    TEACHING_MATERIALS = "teaching_materials", // 教学材料
    TRAVEL = "travel", // 差旅费
    OFFICE_SUPPLIES = "office_supplies", // 办公用品
    EQUIPMENT = "equipment", // 设备
    OTHER = "other" // 其他
}

// 报销记录接口
interface Reimbursement {
    id: string;
    title: string;
    description: string;
    amount: number;
    type: ReimbursementType;
    receipts: string[];
    additionalDocuments?: string[];
    status: ReimbursementStatus;
    reviewComment?: string;
    reviewedBy?: any;
    reviewedAt?: Date;
    paymentMethod?: string;
    bankAccount?: {
        accountName: string;
        accountNumber: string;
        bankName: string;
    };
    paymentDate?: Date;
    createdBy: any;
    createdAt: Date;
    updatedAt: Date;
}

const AllReimbursements: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { setHeaderTheme } = useHeader();
    const [reimbursements, setReimbursements] = useState<Reimbursement[]>([]);
    const [filteredReimbursements, setFilteredReimbursements] = useState<Reimbursement[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const currentLocale = i18n.language === 'zh' ? zhCN : enUS;
    const [statusFilter, setStatusFilter] = useState<string>('all');

    // 分页状态
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10); // 每页显示10条记录
    const [totalPages, setTotalPages] = useState(1);
    const [paginatedReimbursements, setPaginatedReimbursements] = useState<Reimbursement[]>([]);

    useEffect(() => {
        // 设置透明背景和白色文字
        setHeaderTheme({
            bgColor: 'bg-transparent',
            textColor: 'text-white'
        });

        return () => {
            // 恢复默认样式
            setHeaderTheme({
                bgColor: 'bg-white',
                textColor: 'text-[#142b89]'
            });
        };
    }, [setHeaderTheme]);
    useEffect(() => {
        const fetchAllReimbursements = async () => {
            if (!auth.currentUser) return;

            try {
                setLoading(true);
                setError(null);

                const userRef = doc(db, 'users', auth.currentUser.uid);

                // 查询当前用户创建的所有报销记录
                const q = query(
                    collection(db, 'reimbursements'),
                    where('createdBy', '==', userRef),
                    orderBy('createdAt', 'desc')
                );

                const querySnapshot = await getDocs(q);
                const reimbursementsList: Reimbursement[] = [];

                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    reimbursementsList.push({
                        id: doc.id,
                        ...data,
                        createdAt: data.createdAt?.toDate(),
                        updatedAt: data.updatedAt?.toDate(),
                        reviewedAt: data.reviewedAt?.toDate(),
                        paymentDate: data.paymentDate?.toDate(),
                    } as Reimbursement);
                });

                setReimbursements(reimbursementsList);
                setFilteredReimbursements(reimbursementsList);
            } catch (err) {
                console.error('获取报销记录失败:', err);
                setError(t('reimbursements.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        fetchAllReimbursements();
    }, [t]);

    // 当状态筛选器改变时，更新筛选后的报销记录列表
    useEffect(() => {
        if (statusFilter === 'all') {
            setFilteredReimbursements(reimbursements);
        } else {
            setFilteredReimbursements(reimbursements.filter(reimbursement => reimbursement.status === statusFilter));
        }
        setCurrentPage(1); // 重置为第一页
    }, [statusFilter, reimbursements]);

    // 计算分页数据
    useEffect(() => {
        // 计算总页数
        const total = Math.ceil(filteredReimbursements.length / itemsPerPage);
        setTotalPages(total || 1); // 确保至少有1页

        // 获取当前页的数据
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        setPaginatedReimbursements(filteredReimbursements.slice(startIndex, endIndex));
    }, [filteredReimbursements, currentPage, itemsPerPage]);

    // 页面导航函数
    const goToPage = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            // 滚动到页面顶部
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // 获取报销类型的本地化名称
    const getReimbursementTypeName = (type: ReimbursementType) => {
        return t(`reimbursements.types.${type}`);
    };

    // 获取报销状态的本地化名称和样式
    const getStatusInfo = (status: ReimbursementStatus) => {
        switch (status) {
            case ReimbursementStatus.PENDING:
                return {
                    name: t('reimbursements.status.pending'),
                    bgColor: 'bg-yellow-100',
                    textColor: 'text-yellow-800',
                    borderColor: 'border-yellow-200'
                };
            case ReimbursementStatus.APPROVED:
                return {
                    name: t('reimbursements.status.approved'),
                    bgColor: 'bg-green-100',
                    textColor: 'text-green-800',
                    borderColor: 'border-green-200'
                };
            case ReimbursementStatus.REJECTED:
                return {
                    name: t('reimbursements.status.rejected'),
                    bgColor: 'bg-red-100',
                    textColor: 'text-red-800',
                    borderColor: 'border-red-200'
                };
            case ReimbursementStatus.PAID:
                return {
                    name: t('reimbursements.status.paid'),
                    bgColor: 'bg-blue-100',
                    textColor: 'text-blue-800',
                    borderColor: 'border-blue-200'
                };
            default:
                return {
                    name: status,
                    bgColor: 'bg-gray-100',
                    textColor: 'text-gray-800',
                    borderColor: 'border-gray-200'
                };
        }
    };

    // 格式化日期
    const formatDate = (date: Date) => {
        if (!date) return '';
        return format(date, 'PPP', { locale: currentLocale });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-800 to-blue-600 pt-20">
            {/* 顶部背景 */}
            <div className="relative w-full overflow-hidden">
                <div className="absolute inset-0 bg-[url('/images/background.jpg')] bg-cover bg-center opacity-10"></div>

                {/* 内容层 */}
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 z-10">
                    <div className="flex items-center mb-6">
                        <Link to="/profile" className="text-blue-200 hover:text-white mr-4">
                            <ArrowLeftIcon className="h-5 w-5" />
                        </Link>
                        <motion.h1
                            className="text-3xl font-bold text-white"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            {t('reimbursements.allReimbursements')}
                        </motion.h1>
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 space-y-4 md:space-y-0">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                        >
                            <button
                                onClick={() => navigate('/profile/reimbursements/new')}
                                className="px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-md text-blue-100 transition-all flex items-center"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                </svg>
                                {t('reimbursements.addNew')}
                            </button>
                        </motion.div>

                        {/* 筛选器 */}
                        <motion.div
                            className="flex flex-wrap items-center gap-2"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                        >
                            <div className="flex items-center text-blue-200 mr-2">
                                <FunnelIcon className="h-4 w-4 mr-1" />
                                <span>{t('reimbursements.filterByStatus')}:</span>
                            </div>

                            <button
                                onClick={() => setStatusFilter('all')}
                                className={`px-3 py-1.5 rounded-full text-sm ${statusFilter === 'all'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-blue-900/50 text-blue-200 hover:bg-blue-800/70'
                                    }`}
                            >
                                {t('reimbursements.allStatuses')}
                            </button>

                            <button
                                onClick={() => setStatusFilter(ReimbursementStatus.PENDING)}
                                className={`px-3 py-1.5 rounded-full text-sm ${statusFilter === ReimbursementStatus.PENDING
                                        ? 'bg-yellow-600 text-white'
                                        : 'bg-blue-900/50 text-blue-200 hover:bg-blue-800/70'
                                    }`}
                            >
                                {t('reimbursements.status.pending')}
                            </button>

                            <button
                                onClick={() => setStatusFilter(ReimbursementStatus.APPROVED)}
                                className={`px-3 py-1.5 rounded-full text-sm ${statusFilter === ReimbursementStatus.APPROVED
                                        ? 'bg-green-600 text-white'
                                        : 'bg-blue-900/50 text-blue-200 hover:bg-blue-800/70'
                                    }`}
                            >
                                {t('reimbursements.status.approved')}
                            </button>

                            <button
                                onClick={() => setStatusFilter(ReimbursementStatus.REJECTED)}
                                className={`px-3 py-1.5 rounded-full text-sm ${statusFilter === ReimbursementStatus.REJECTED
                                        ? 'bg-red-600 text-white'
                                        : 'bg-blue-900/50 text-blue-200 hover:bg-blue-800/70'
                                    }`}
                            >
                                {t('reimbursements.status.rejected')}
                            </button>

                            <button
                                onClick={() => setStatusFilter(ReimbursementStatus.PAID)}
                                className={`px-3 py-1.5 rounded-full text-sm ${statusFilter === ReimbursementStatus.PAID
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-blue-900/50 text-blue-200 hover:bg-blue-800/70'
                                    }`}
                            >
                                {t('reimbursements.status.paid')}
                            </button>

                            {statusFilter !== 'all' && (
                                <button
                                    onClick={() => setStatusFilter('all')}
                                    className="ml-2 p-1 rounded-full bg-blue-800/50 text-blue-200 hover:bg-blue-700/70"
                                    title={t('common.clear')}
                                >
                                    <XMarkIcon className="h-4 w-4" />
                                </button>
                            )}
                        </motion.div>
                    </div>

                    {/* 分页信息 */}
                    {!loading && !error && filteredReimbursements.length > 0 && (
                        <motion.div
                            className="text-blue-200 text-sm mb-4"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            {t('reimbursements.showingReimbursements', {
                                start: (currentPage - 1) * itemsPerPage + 1,
                                end: Math.min(currentPage * itemsPerPage, filteredReimbursements.length),
                                total: filteredReimbursements.length
                            })}
                        </motion.div>
                    )}
                </div>
            </div>

            {/* 主要内容区域 */}
            <div className="relative pb-20 z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
                    {loading ? (
                        <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                            <div className="flex justify-center items-center py-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-transparent"></div>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                            <div className="text-center py-8">
                                <p className="text-red-300">{error}</p>
                                <button
                                    onClick={() => window.location.reload()}
                                    className="mt-4 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-md text-red-100 transition-all"
                                >
                                    {t('common.retry')}
                                </button>
                            </div>
                        </div>
                    ) : filteredReimbursements.length === 0 ? (
                        <div className="p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                            <div className="text-center py-8">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-blue-300/50 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <p className="text-blue-100 mb-4">
                                    {statusFilter === 'all'
                                        ? t('reimbursements.noRecords')
                                        : t('reimbursements.noReimbursementsWithStatus', { status: t(`reimbursements.status.${statusFilter}`) })}
                                </p>
                                <button
                                    onClick={() => navigate('/profile/reimbursements/new')}
                                    className="px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-md text-blue-100 transition-all"
                                >
                                    {t('reimbursements.createFirst')}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-4">
                                {paginatedReimbursements.map((reimbursement, index) => {
                                    const statusInfo = getStatusInfo(reimbursement.status);

                                    return (
                                        <motion.div
                                            key={reimbursement.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.3, delay: index * 0.05 }}
                                            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4 hover:bg-white/10 transition-all cursor-pointer"
                                            onClick={() => navigate(`/profile/reimbursements/${reimbursement.id}`)}
                                        >
                                            <div className="flex flex-col md:flex-row md:items-center justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center">
                                                        <h3 className="text-lg font-semibold text-white">{reimbursement.title}</h3>
                                                        <span className={`ml-3 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.textColor} border ${statusInfo.borderColor}`}>
                                                            {statusInfo.name}
                                                        </span>
                                                    </div>
                                                    <div className="mt-1 text-sm text-blue-100">
                                                        <span className="inline-block mr-4">
                                                            <span className="opacity-70">{t('reimbursements.amount')}:</span> ${reimbursement.amount.toFixed(2)}
                                                        </span>
                                                        <span className="inline-block mr-4">
                                                            <span className="opacity-70">{t('reimbursements.type')}:</span> {getReimbursementTypeName(reimbursement.type)}
                                                        </span>
                                                        <span className="inline-block">
                                                            <span className="opacity-70">{t('reimbursements.createdAt')}:</span> {formatDate(reimbursement.createdAt)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="mt-2 md:mt-0">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-300/70" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>

                            {/* 分页控制器 */}
                            {totalPages > 1 && (
                                <motion.div
                                    className="flex justify-center mt-10"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.3 }}
                                >
                                    <nav className="flex items-center space-x-2">
                                        <button
                                            onClick={() => goToPage(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className={`p-2 rounded-full ${currentPage === 1
                                                    ? 'text-blue-300/50 cursor-not-allowed'
                                                    : 'text-blue-200 hover:bg-blue-800/50 hover:text-white'
                                                }`}
                                            aria-label={t('common.previous')}
                                        >
                                            <ChevronLeftIcon className="h-5 w-5" />
                                        </button>

                                        <div className="flex items-center space-x-1">
                                            {/* 显示页码 */}
                                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                                .filter(page => {
                                                    // 显示当前页、第一页、最后一页，以及当前页附近的页码
                                                    return page === 1 ||
                                                        page === totalPages ||
                                                        (page >= currentPage - 1 && page <= currentPage + 1);
                                                })
                                                .map((page, index, array) => {
                                                    // 如果页码不连续，显示省略号
                                                    const showEllipsisBefore = index > 0 && page > array[index - 1] + 1;
                                                    const showEllipsisAfter = index < array.length - 1 && page < array[index + 1] - 1;

                                                    return (
                                                        <React.Fragment key={page}>
                                                            {showEllipsisBefore && (
                                                                <span className="px-3 py-1.5 text-blue-300">...</span>
                                                            )}

                                                            <button
                                                                onClick={() => goToPage(page)}
                                                                className={`px-3 py-1.5 rounded-md ${currentPage === page
                                                                        ? 'bg-blue-600 text-white font-medium'
                                                                        : 'text-blue-200 hover:bg-blue-800/50 hover:text-white'
                                                                    }`}
                                                            >
                                                                {page}
                                                            </button>

                                                            {showEllipsisAfter && (
                                                                <span className="px-3 py-1.5 text-blue-300">...</span>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })
                                            }
                                        </div>

                                        <button
                                            onClick={() => goToPage(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className={`p-2 rounded-full ${currentPage === totalPages
                                                    ? 'text-blue-300/50 cursor-not-allowed'
                                                    : 'text-blue-200 hover:bg-blue-800/50 hover:text-white'
                                                }`}
                                            aria-label={t('common.next')}
                                        >
                                            <ChevronRightIcon className="h-5 w-5" />
                                        </button>
                                    </nav>
                                </motion.div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AllReimbursements;
